import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import { useCardDraft } from "../common/useCardDraft";
import { toIsoUtc } from "../chiefComplaints/dateHelpers";
import MedicineService from "./MedicineService";
import UnitPicker from "./UnitPicker";
import {
  SegStrip,
  AS_NEEDED,
  clampDoseValue,
  isPositiveDose,
  fetchFormulationCatalog,
  getFormulationCatalog,
} from "./MedicineForm";
import DiagnosisPicker from "./DiagnosisPicker";
import { MED_V2_REFRESH } from "./events";
import {
  MEDICINE_STATUS,
  medicineBand,
  DOSE_QUICKPICK,
  TIMES_OF_DAY,
  INTERVAL_OPTIONS,
  FREQ_UNIT_OPTIONS,
  INSTRUCTION_OPTIONS,
  DURATION_DAYS_QUICKPICK,
  DURATION_LONG_QUICKPICK,
} from "./data";

/**
 * Update sheet for one medicine — the centred UpdateStatusModal moved into
 * the editor rail beside the Add sheet, the same treatment every other v3
 * section's update surface got. Same med3 control vocabulary as the Add
 * sheet (board 2964:4270); the fields are the modal's — the full regimen is
 * editable on an update so the audit captures the complete new state, with
 * the three-value status select on top.
 *
 * An update sheet is never blank, so it reports itself filled on mount, and
 * it owns its write: a just-in-time version read, then POST …/status.
 */

const MAX_DESCRIPTION_WORDS = 250;
const countWords = (s) => {
  const t = (s || "").trim();
  return t ? t.split(/\s+/).length : 0;
};
const clampWords = (next, max) => {
  if (countWords(next) <= max) return next;
  const parts = next.split(/(\s+)/);
  let words = 0;
  const out = [];
  for (const chunk of parts) {
    if (/^\s+$/.test(chunk)) { out.push(chunk); continue; }
    if (chunk === "") continue;
    if (words >= max) break;
    out.push(chunk);
    words += 1;
  }
  return out.join("").replace(/\s+$/, "");
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// Same shape as MedicineForm.js addDays — kept local so the Update
// surface can derive its own end-of-regimen date without pulling the
// Add form's module just for a helper. Returns null for bad input.
const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  if (isNaN(d.getTime()) || !days) return null;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const CollapseChevron = () => (
  <svg viewBox="0 0 11 11" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M2 7.5L5.5 3.5L9 7.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
    <path
      d="M13.3333 2H12.6667V0.666667H11.3333V2H4.66667V0.666667H3.33333V2H2.66667C1.93333 2 1.33333 2.6 1.33333 3.33333V14C1.33333 14.7333 1.93333 15.3333 2.66667 15.3333H13.3333C14.0667 15.3333 14.6667 14.7333 14.6667 14V3.33333C14.6667 2.6 14.0667 2 13.3333 2ZM13.3333 14H2.66667V6.66667H13.3333V14ZM13.3333 5.33333H2.66667V3.33333H13.3333V5.33333Z"
      fill="#26284D"
    />
  </svg>
);

const MedicineStatusForm = ({
  patientId,
  formId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
  menuClassName,
}) => {
  const medicine = editing;
  const cur = (medicine && medicine.current) || {};
  const dq = cur.doseQuantity || {};
  const df = cur.doseFrequency || {};

  // ZY-10356 Phase 1 — transcript-mode draft preservation. Standalone
  // mounts return [null, no-op]; the hydration + sync are inert
  // outside the modal.
  const [draft, saveDraft] = useCardDraft(formId);

  const [status, setStatus] = useState(() => (
    draft && "status" in draft ? draft.status : (medicine.status || "ongoing")
  ));
  // ZY-10352 Req 5 — Start date defaults to TODAY on every open of the
  // update card, overriding whatever the record previously stored. The
  // reviewer can edit back to any historical date (field stays
  // editable), and the restore-on-terminate rule below rolls it back to
  // the stored value the moment Status flips to Discontinued or
  // Completed so the audit's "started on … ended on …" window still
  // spans the real episode.
  //
  // EXCEPTION — Past medicines (Completed / Discontinued / Deleted)
  // open the Update card as a read-only VIEW, not an edit surface, so
  // every field must reflect the DB verbatim (the stored Start date,
  // and the derived End date that follows from it). Seeding today here
  // would also pull the End-date hint forward past where the record
  // actually sat. Guard uses the same `wasPast` check `readOnly` is
  // derived from (defined below, so we recompute it here from
  // `medicine.status` to stay before the hook ordering).
  const _wasPastOnOpen = medicine && (
    medicine.status === "completed" ||
    medicine.status === "discontinued" ||
    medicine.status === "deleted"
  );
  const [medStartDate, setMedStartDate] = useState(() => {
    if (draft && "medStartDate" in draft) return draft.medStartDate;
    if (_wasPastOnOpen && cur.medStartDate) {
      return String(cur.medStartDate).slice(0, 10);
    }
    return todayISO();
  });
  // ZY-10352 wave-2 — the form no longer carries a user-input "Updated
  // on" field. UpdatedAt is captured server-side as the wall-clock of
  // the write; the reviewer-picked EFFECTIVE discontinue date is now a
  // separate `discontinuedDate` input that only surfaces when the
  // reviewer flips Status to Discontinued. For an already-discontinued
  // past medicine (readOnly view) the field is seeded from the live
  // StatusEntry's DiscontinuedDate so the reviewer sees the recorded
  // clinical date; blank otherwise (reviewer picks). Cleared again
  // whenever the status moves away from discontinued (see changeStatus
  // below).
  const [discontinuedDate, setDiscontinuedDate] = useState(() => {
    if (draft && "discontinuedDate" in draft) return draft.discontinuedDate;
    if (medicine && medicine.status === "discontinued") {
      const hist = Array.isArray(medicine.statusHistory) ? medicine.statusHistory : [];
      for (let i = hist.length - 1; i >= 0; i -= 1) {
        const e = hist[i];
        if (!e || e.event === "delete") continue;
        if (e.status === "discontinued" && e.discontinuedDate) {
          return String(e.discontinuedDate).slice(0, 10);
        }
      }
    }
    return "";
  });
  const [ongoing, setOngoing] = useState(() => (
    draft && "ongoing" in draft ? draft.ongoing : (cur.ongoingIndefinitely || false)
  ));
  // Duration state has to split cleanly across the two quick-pick rows
  // and the custom-days input so the Update card reflects whatever
  // shape the record was saved in:
  //   • Row 1 (3 / 7 / 10 / 14 / 28)     → durationDays = N,   customDays = ""
  //   • Row 2 (1w / 2w / 3w / 4w / 2m)   → durationDays = N,   customDays = ""
  //       (the LABEL also drives highlight via `lastPickRef` below)
  //   • Custom (anything else, e.g. 21)  → durationDays = 0,   customDays = "N"
  // Without the custom-days seed below, a record saved as 21 days
  // opened in the Update card showed no highlight anywhere and an
  // empty custom input.
  const QUICKPICK_DAYS_SET = new Set(DURATION_DAYS_QUICKPICK);
  const LONG_DAYS_SET = new Set(DURATION_LONG_QUICKPICK.map((d) => d.days));
  const storedIsQuickpick = QUICKPICK_DAYS_SET.has(cur.durationDays) || LONG_DAYS_SET.has(cur.durationDays);
  const [durationDays, setDurationDays] = useState(() => (
    draft && "durationDays" in draft
      ? draft.durationDays
      : ((cur.durationDays && storedIsQuickpick) ? cur.durationDays : 0)
  ));
  const [customDays, setCustomDays] = useState(() => (
    draft && "customDays" in draft
      ? draft.customDays
      : ((cur.durationDays && !storedIsQuickpick) ? String(cur.durationDays) : "")
  ));
  const [doseValue, setDoseValue] = useState(() => (
    draft && "doseValue" in draft ? draft.doseValue : (dq.value || "")
  ));
  const [doseUnit, setDoseUnit] = useState(() => (
    draft && "doseUnit" in draft ? draft.doseUnit : (dq.unit || "")
  ));
  // Redesigned dose model (mirrors MedicineForm) — see field comments
  // on model.MedicineState. Per-slot map is populated when the record
  // was saved in Times-of-day mode with named slots; empty otherwise.
  const [perSlotQuantities, setPerSlotQuantities] = useState(() => {
    if (draft && "perSlotQuantities" in draft) return draft.perSlotQuantities;
    return (cur.perSlotQuantities && typeof cur.perSlotQuantities === "object")
      ? { ...cur.perSlotQuantities }
      : {};
  });
  const [timesOfDay, setTimesOfDay] = useState(() => (
    draft && "timesOfDay" in draft ? draft.timesOfDay : (df.timesOfDay || [])
  ));
  const [customFreqValue, setCustomFreqValue] = useState(() => (
    draft && "customFreqValue" in draft
      ? draft.customFreqValue
      : (df.customValue ? String(df.customValue) : "")
  ));
  const [customFreqUnit, setCustomFreqUnit] = useState(() => (
    draft && "customFreqUnit" in draft ? draft.customFreqUnit : (df.customUnit || "Hours")
  ));
  // Explicit mode toggle for the whole frequency block. Seeded from
  // whatever the record already carries: perSlot or named timesOfDay
  // slots → "timesOfDay"; custom pair → "custom".
  const [frequencyMode, setFrequencyMode] = useState(() => {
    if (draft && "frequencyMode" in draft) return draft.frequencyMode;
    const hasSlots = (df.timesOfDay || []).length > 0;
    const hasPerSlot = cur.perSlotQuantities && Object.keys(cur.perSlotQuantities || {}).length > 0;
    if (hasSlots || hasPerSlot) return "timesOfDay";
    if (df.customValue) return "custom";
    return "timesOfDay";
  });
  // Formulation catalog — REFLD → { label, units, instructions }.
  // Reuses the module-level cache in MedicineForm so every form
  // instance shares ONE fetch.
  const [formulationCatalog, setFormulationCatalog] = useState(
    () => getFormulationCatalog() || {},
  );
  useEffect(() => {
    let alive = true;
    fetchFormulationCatalog().then((r) => {
      if (!alive || !r) return;
      if (Object.keys(r).length) setFormulationCatalog(r);
    });
    return () => { alive = false; };
  }, []);
  const [instructions, setInstructions] = useState(() => (
    draft && "instructions" in draft ? draft.instructions : (cur.instructions || "")
  ));
  const [diagnosisId, setDiagnosisId] = useState(() => (
    draft && "diagnosisId" in draft ? draft.diagnosisId : (medicine.diagnosisId || null)
  ));
  // Seed Description from the existing record so the clinician sees the
  // note that was recorded on the last write — matches the "load all
  // config to fields on update" rule.
  const [reason, setReason] = useState(() => (
    draft && "reason" in draft ? draft.reason : (cur.description || "")
  ));

  const [collapsed, setCollapsed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // ZY-10356 Phase 1 — sync persistent fields to the modal-root draft
  // store on every change. Ephemeral state (collapsed, submitting,
  // feedback, formulationCatalog) is deliberately excluded.
  useEffect(() => {
    saveDraft({
      status, medStartDate, discontinuedDate, ongoing,
      durationDays, customDays,
      doseValue, doseUnit, perSlotQuantities,
      timesOfDay, customFreqValue, customFreqUnit, frequencyMode,
      instructions, diagnosisId, reason,
    });
  }, [
    saveDraft,
    status, medStartDate, discontinuedDate, ongoing,
    durationDays, customDays,
    doseValue, doseUnit, perSlotQuantities,
    timesOfDay, customFreqValue, customFreqUnit, frequencyMode,
    instructions, diagnosisId, reason,
  ]);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  // Seed the "which duration cell was last picked" ref so the saved
  // regimen highlights the right quick-pick cell on Update open.
  //   • 21 days → "3 weeks" (unique to Row 2)
  //   • 60 days → "2 months" (unique to Row 2)
  //   • 3 / 7 / 10 / 14 / 28 → Row 1 string (the shorter default when
  //     a value overlaps both rows, e.g. 7 ↔ "1 week")
  //   • Everything else → "" (custom value or nothing stored; the
  //     custom input lights up on its own via `customDays`).
  const lastPickRef = useRef((() => {
    const n = cur.durationDays;
    if (!n) return "";
    const longOnly = DURATION_LONG_QUICKPICK.find(
      (d) => d.days === n && !QUICKPICK_DAYS_SET.has(n),
    );
    if (longOnly) return longOnly.label;
    if (QUICKPICK_DAYS_SET.has(n)) return String(n);
    return "";
  })());

  // Rail contract: an update sheet is never blank.
  useEffect(() => {
    onFilledChange(formId, true);
  }, [formId, onFilledChange]);

  // Same mutex + mode-toggle rules as the Add sheet — see MedicineForm
  // for the full rationale.
  // Preserve the other mode's state on switch — the reviewer can
  // toggle to preview the alternate shape (or restore an earlier pick)
  // without losing what's already there. The save body filters by
  // `frequencyMode` so only the active mode's values reach the wire.
  const switchFrequencyMode = (next) => {
    if (next === frequencyMode) return;
    // Custom mode shows the scalar Qty + Unit pair — seed the unit if
    // the scalar is blank so the picker doesn't drop to "None".
    if (next === "custom") seedScalarUnitIfEmpty();
    setFrequencyMode(next);
  };

  // Seed unit for a NEW per-slot row — same rule as MedicineForm:
  // picked scalar unit > first Recommended unit (ZY-10312 — multi-unit
  // formulations were previously falling through to "" and seeding
  // the slot as None, which greyed out the Qty box with no signal
  // that a unit was expected) > "" when no Recommended units at all.
  const seedSlotUnit = () => doseUnit || availableUnits[0] || "";

  // Seed the SCALAR doseUnit when the UI is about to switch to a view
  // that reads it (As-needed row, Custom frequency row). A per-slot
  // record whose scalar unit is empty would otherwise land in the new
  // view with "None" + a disabled Qty. Prefer any per-slot unit the
  // reviewer already picked (round-trip their intent); fall back to
  // the first Recommended. No-op when the scalar is already set or
  // the catalog has no Recommended units.
  const seedScalarUnitIfEmpty = () => {
    if (doseUnit) return;
    const anySlotUnit = Object.values(perSlotQuantities)
      .map((q) => q && q.unit)
      .find(Boolean);
    const seed = anySlotUnit || availableUnits[0] || "";
    if (seed) setDoseUnit(seed);
  };

  // Per-slot quantity setters — mirror MedicineForm's UX-only fan-out.
  // First filled slot copies its value / unit to every other selected
  // slot IF the others are all empty. Once any other slot carries a
  // value / unit the copy stops firing, so a differentiated regimen
  // the reviewer set intentionally is never overwritten.
  const setSlotValue = (slot, value) => {
    setPerSlotQuantities((prev) => {
      // Guard: a None-unit slot never carries a qty. If the picker
      // somehow calls onChange on a disabled input (autofill, paste,
      // race with the disabled attr taking effect), swallow the value.
      const currentUnit = (prev[slot] && prev[slot].unit) || "";
      if (!currentUnit) return prev;
      // Rule 1: qty change NEVER touches units — this slot's unit is
      // preserved as-is, and the fan-out below leaves every other slot's
      // unit alone. A qty change is a qty change.
      const nextRow = { value, unit: currentUnit };
      // Rule 3: cascade the qty into the slots BELOW only when the
      // source is the FIRST slot AND every (non-None) below slot still
      // tracks the source's PREVIOUS value. That gives keystroke-by-
      // keystroke mirroring ("4" → "45" → "456") while the reviewer is
      // typing at the top; the moment they touch any below slot the
      // mirror stops. Typing in any slot other than the first is always
      // row-local — no cascade. None-unit below slots are ignored.
      const isFirstSlot = namedSlots.length > 1 && slot === namedSlots[0];
      const sourceOld = (prev[slot] && prev[slot].value) || "";
      const fanOut =
        isFirstSlot &&
        namedSlots.every((s) => {
          if (s === slot) return true;
          const su = (prev[s] && prev[s].unit) || "";
          if (!su) return true;
          return ((prev[s] && prev[s].value) || "") === sourceOld;
        });
      if (!fanOut) return { ...prev, [slot]: nextRow };
      const next = { ...prev, [slot]: nextRow };
      namedSlots.forEach((s) => {
        if (s === slot) return;
        const siblingUnit = (prev[s] && prev[s].unit) || "";
        // A sibling whose unit is None never receives the cascaded qty.
        // Preserve its own value (which should already be blank because
        // the qty box is disabled when unit is None).
        if (!siblingUnit) {
          next[s] = { value: (prev[s] && prev[s].value) || "", unit: "" };
          return;
        }
        // Preserve sibling's own unit (rule 1 forbids us from touching
        // it here).
        next[s] = { value, unit: siblingUnit };
      });
      return next;
    });
  };
  const setSlotUnit = (slot, unit) => {
    setPerSlotQuantities((prev) => {
      // Picking None (empty unit) clears the paired Qty so a stale value
      // doesn't linger in a greyed-out box on save/reload.
      const value = unit ? ((prev[slot] && prev[slot].value) || "") : "";
      const nextRow = { value, unit };
      // Rule 4: cascade the unit only from the FIRST slot and only
      // while every below slot is still on None. Once any below slot
      // has its own unit picked, unit changes on the first slot stay
      // row-local. Picking None on the first slot also stays local
      // (None doesn't cascade).
      const isFirstSlot = namedSlots.length > 1 && slot === namedSlots[0];
      const fanOut =
        isFirstSlot &&
        String(unit).trim() !== "" &&
        namedSlots.every((s) => s === slot
          ? true
          : !String((prev[s] && prev[s].unit) || "").trim());
      if (!fanOut) return { ...prev, [slot]: nextRow };
      const next = { ...prev, [slot]: nextRow };
      namedSlots.forEach((s) => {
        if (s === slot) return;
        next[s] = { value: (prev[s] && prev[s].value) || "", unit };
      });
      return next;
    });
  };

  const toggleTimeOfDay = (t) => {
    if (t === AS_NEEDED) {
      const enteringAsNeeded = !timesOfDay.includes(AS_NEEDED);
      setTimesOfDay((prev) => {
        const has = prev.includes(AS_NEEDED);
        if (has) return prev.filter((x) => x !== AS_NEEDED);
        return [AS_NEEDED];
      });
      setPerSlotQuantities({});
      // As-needed renders the scalar Qty + Unit pair, so seed the
      // scalar unit (ZY-10312) if the record came in per-slot with a
      // blank scalar — otherwise the UnitPicker drops to "None" and
      // the Qty input is disabled on arrival.
      if (enteringAsNeeded) seedScalarUnitIfEmpty();
      return;
    }
    const seedUnit = seedSlotUnit();
    setTimesOfDay((prev) => {
      const withoutAsNeeded = prev.filter((x) => x !== AS_NEEDED);
      if (withoutAsNeeded.includes(t)) {
        setPerSlotQuantities((qs) => {
          const { [t]: _unused, ...rest } = qs;
          return rest;
        });
        return withoutAsNeeded.filter((x) => x !== t);
      }
      setPerSlotQuantities((qs) => ({
        ...qs,
        [t]: qs[t] || { value: "", unit: seedUnit },
      }));
      return [...withoutAsNeeded, t];
    });
  };
  // Interval chips are pure shortcuts for the custom (value + unit)
  // pair below. See MedicineForm.pickInterval for the full rationale.
  const pickInterval = (iv) => {
    const n = iv.replace(/h$/, "");
    if (customFreqValue === n && customFreqUnit === "Hours") {
      setCustomFreqValue("");
      return;
    }
    setCustomFreqValue(n);
    setCustomFreqUnit("Hours");
  };
  const intervalChipMatches = (iv) => {
    const n = iv.replace(/h$/, "");
    return customFreqValue === n && customFreqUnit === "Hours";
  };
  // Per-unit bounds — mirrors MedicineForm:
  //   Hours              → [1, 23]
  //   Days/Weeks/Months  → [1, 999]
  const customFreqMax = (u) => (u === "Hours" ? 23 : 999);
  const clampCustomFreq = (v, u) => {
    if (v === "") return v;
    const n = parseInt(v, 10);
    if (isNaN(n)) return v;
    const max = customFreqMax(u);
    if (n > max) return String(max);
    if (n < 1)   return "1";
    return v;
  };
  const changeCustomFreqValue = (v) => {
    setCustomFreqValue(clampCustomFreq(v, customFreqUnit));
  };
  const changeCustomFreqUnit = (u) => {
    if (customFreqValue) {
      const clamped = clampCustomFreq(customFreqValue, u);
      if (clamped !== customFreqValue) setCustomFreqValue(clamped);
    }
    setCustomFreqUnit(u);
  };

  const pickDays = (n) => {
    setDurationDays(n);
    setCustomDays("");
  };

  // Ending a course (status → completed/discontinued) locks the regimen
  // block — dose, frequency, instructions, duration, start-date — because
  // those describe an active regimen. Only Updated on + Description stay
  // editable so the audit can capture WHEN the course ended and WHY.
  const isEnded = status !== "ongoing";
  const regimenDisabled = submitting || isEnded;
  // ZY-10352 wave-2 — past medicines (Completed / Discontinued / Deleted)
  // now render the Update card READ-ONLY: every input is disabled and
  // the Save button is suppressed. Reviewer sees the state and closes
  // the sheet. Reopen / reclassify flows through the Add-medicine sheet
  // instead (ICD dedup triggers reactivation).
  const wasPast =
    medicine.status === "completed" ||
    medicine.status === "discontinued" ||
    medicine.status === "deleted";
  const statusDisabled = submitting || wasPast;
  const readOnly = wasPast;

  // Snapshot of the SEEDED form values at mount — used by the dirty check
  // below to disable Save until the clinician actually changes something.
  // Prevents no-op audit entries. Kept in a ref so it survives re-renders
  // but never re-captures on state updates.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    initialRef.current = {
      status: medicine.status || "ongoing",
      // ZY-10352 Req 5 — Start date opens defaulted to today for the
      // editable flow and to the stored value for the read-only past
      // view; mirror whichever branch the useState initializer took so
      // simply opening the sheet doesn't flip isDirty = true.
      medStartDate: _wasPastOnOpen && cur.medStartDate
        ? String(cur.medStartDate).slice(0, 10)
        : todayISO(),
      // Seeded to match the useState initializer above so opening the
      // Update card for an already-discontinued (past) medicine doesn't
      // falsely trip the dirty check.
      discontinuedDate: discontinuedDate,
      ongoing: !!cur.ongoingIndefinitely,
      // Mirror the useState initializers above — a custom stored
      // duration seeds `customDays` and leaves `durationDays` at 0, so
      // the dirty check has to compare against the SAME shape or just
      // opening the sheet would read as dirty.
      durationDays: (cur.durationDays && storedIsQuickpick) ? cur.durationDays : 0,
      customDays: (cur.durationDays && !storedIsQuickpick) ? String(cur.durationDays) : "",
      doseValue: dq.value || "",
      doseUnit: dq.unit || "",
      perSlotQuantitiesJSON: JSON.stringify(cur.perSlotQuantities || {}),
      frequencyMode: (() => {
        const hasSlots = (df.timesOfDay || []).length > 0;
        const hasPerSlot = cur.perSlotQuantities && Object.keys(cur.perSlotQuantities || {}).length > 0;
        if (hasSlots || hasPerSlot) return "timesOfDay";
        if (df.customValue) return "custom";
        return "timesOfDay";
      })(),
      timesOfDay: (df.timesOfDay || []).slice(),
      customFreqValue: (customFreqValue || ""),
      customFreqUnit: (customFreqUnit || "Hours"),
      instructions: cur.instructions || "",
      diagnosisId: medicine.diagnosisId || null,
      reason: cur.description || "",
    };
  }

  const effectiveDurationDays = ongoing
    ? 0
    : customDays
    ? Math.max(0, parseInt(customDays, 10) || 0)
    : durationDays;

  // ─── ZY-10352: Updated on + Status derived state ─────────────────────
  // Original seed values from the stored record — compared against the
  // CURRENT form state to detect "duration changed" (which includes a
  // start-date change; both move the derived end date). Not kept in a
  // ref because the record itself doesn't change for the sheet's
  // lifetime — plain locals are enough.
  const originalStartISO = cur.medStartDate ? cur.medStartDate.slice(0, 10) : "";
  const originalDurationDays = cur.durationDays || 0;
  const originalOngoing = !!cur.ongoingIndefinitely;

  // End-of-regimen date derived from the form's live start + duration.
  // Null when the regimen is ongoing indefinitely OR duration isn't a
  // positive int. Mirrors the ZY-10312 Add-form fix:
  // addDays(startDate, days - 1) because the start date itself is day 1.
  const derivedEndDate = (!ongoing && medStartDate && effectiveDurationDays > 0)
    ? addDays(medStartDate, effectiveDurationDays - 1)
    : null;

  // STORED end date computed from the record's own start + duration —
  // separate from derivedEndDate because Req 5 defaults the live
  // medStartDate to today on open, which would otherwise push
  // derivedEndDate into the future and leave the Completed gate
  // perpetually closed. Completed-selectability is a product of the
  // real course (did the regimen the clinician originally prescribed
  // actually end?), not of whatever the live form currently shows.
  const originalEndDate = (!originalOngoing && originalStartISO && originalDurationDays > 0)
    ? addDays(originalStartISO, originalDurationDays - 1)
    : null;

  // Status "Completed" selectability — the backend's cron auto-flips
  // once the originally-prescribed end date passes, so the reviewer
  // should never be able to manually mark the status Completed earlier.
  // Guarded on the STORED end date, not the live derived one.
  const isAfterEndDate = !!originalEndDate && todayISO() >= originalEndDate;

  // ZY-10352 wave-2 — the form's user-input dates split cleanly along
  // status now:
  //   • Ongoing / Completed → just show the derived END DATE as a
  //     read-only hint. UpdatedAt is a server-side stamp (= now).
  //   • Discontinued        → still show the read-only End date AND
  //     require a reviewer-picked DISCONTINUED DATE within
  //     [medStartDate, derivedEndDate - 1] (strict < endDate per
  //     product). The old "Updated on" input (which overloaded both
  //     roles) is gone.
  const showDiscontinuedDate = status === "discontinued";
  const discontinuedDateMax = derivedEndDate
    ? addDays(derivedEndDate, -1) || derivedEndDate
    : todayISO();

  // Reset every regimen + metadata field to the record's stored values.
  // Called from changeStatus below so a mid-edit status flip never leaves
  // the form carrying a user-tweaked duration (or dose, or instructions
  // etc.) that would then poison the derived "updated on" the mode logic
  // reads. medStartDate and updatedOn are intentionally NOT touched here
  // — the Req 5 start-date rules + the mode-driven updatedOn effect own
  // those on their own axes.
  const resetFieldsToStored = () => {
    setOngoing(!!cur.ongoingIndefinitely);
    // Same quick-pick vs custom split the useState seeds use. A reset
    // after a status flip has to put the custom input back in sync too,
    // not just the quick-pick cells.
    setDurationDays((cur.durationDays && storedIsQuickpick) ? cur.durationDays : 0);
    setCustomDays((cur.durationDays && !storedIsQuickpick) ? String(cur.durationDays) : "");
    setDoseValue(dq.value || "");
    setDoseUnit(dq.unit || "");
    setPerSlotQuantities(
      cur.perSlotQuantities && typeof cur.perSlotQuantities === "object"
        ? { ...cur.perSlotQuantities }
        : {},
    );
    setTimesOfDay((df.timesOfDay || []).slice());
    setCustomFreqValue(df.customValue ? String(df.customValue) : "");
    setCustomFreqUnit(df.customUnit || "Hours");
    setFrequencyMode(() => {
      const hasSlots = (df.timesOfDay || []).length > 0;
      const hasPerSlot = cur.perSlotQuantities && Object.keys(cur.perSlotQuantities || {}).length > 0;
      if (hasSlots || hasPerSlot) return "timesOfDay";
      if (df.customValue) return "custom";
      return "timesOfDay";
    });
    setInstructions(cur.instructions || "");
    setDiagnosisId(medicine.diagnosisId || null);
    setReason(cur.description || "");
  };

  // Status change wrapper — Req 5 keeps Start date in step with the
  // flow of the picked status so the "updated on" window stays sensible:
  //   • Flip to Discontinued / Completed  → RESTORE stored start date so
  //     the terminate audit lands inside the real episode window instead
  //     of a today-overridden one.
  //   • Flip back to Ongoing              → re-apply the Req 5 "today"
  //     override so the auto-end branch below immediately derives
  //     Updated on = today + duration (the re-opened case the reviewer
  //     described: Discontinue → Ongoing should restore the as-opened
  //     derived-end UX, not freeze on the stored start date).
  //
  // Also resets every OTHER regimen + metadata field (duration, dose,
  // frequency, instructions, diagnosis, description) back to the stored
  // record. A user who tweaked duration while Ongoing then flipped to
  // Discontinued would otherwise have the derivedEndDate — and the
  // Updated on bound built on top of it — reflect the half-typed value
  // instead of the real episode's end date.
  const changeStatus = (next) => {
    if (next === status) return;
    setStatus(next);
    resetFieldsToStored();
    // ZY-10352 wave-2 — Discontinued date is reviewer-input, blank by
    // default. Clear on every status flip so an abandoned pick on an
    // earlier Discontinue attempt can't ride into a subsequent save on
    // a different status.
    setDiscontinuedDate("");
    if (next === "discontinued" || next === "completed") {
      if (originalStartISO) setMedStartDate(originalStartISO);
    } else if (next === "ongoing") {
      setMedStartDate(todayISO());
    }
  };
  // ─── /ZY-10352 ────────────────────────────────────────────────────────

  // Custom-frequency pair rule — same as the Add sheet: value + unit are
  // both-or-neither. The pair check runs even when the regimen block is
  // locked (isEnded) because the pair might have been half-typed before
  // the clinician flipped Status back to Completed / Discontinued.
  const customFreqPairOK = !customFreqValue || !!customFreqUnit;

  // Frequency validity depends on mode — same rule as MedicineForm.
  const hasFrequency =
    frequencyMode === "timesOfDay"
      ? timesOfDay.length > 0
      : !!customFreqValue && !!customFreqUnit;

  // As-needed / per-slot dose rules — mirror MedicineForm. Ointment
  // family formulations use the same Qty + unit controls as any other
  // formulation; the "Local application" chip lives in Instructions,
  // NOT in the Dose block.
  const catalogEntry = formulationCatalog[medicine.formulation];
  const availableUnits = (catalogEntry && catalogEntry.units) || [];
  const catalogInstructions = (catalogEntry && catalogEntry.instructions) || [];
  // Global unit vocabulary — same derivation as MedicineForm.
  const allUnits = React.useMemo(() => {
    const s = new Set();
    Object.values(formulationCatalog).forEach((e) => {
      if (!e || !Array.isArray(e.units)) return;
      e.units.forEach((u) => { if (u) s.add(u); });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [formulationCatalog]);
  const otherUnits = allUnits.filter((u) => !availableUnits.includes(u));
  const availableInstructions = catalogInstructions
    .map((k) => INSTRUCTION_OPTIONS.find((o) => o.key === k))
    .filter(Boolean);
  // Auto-align scalar + per-slot units with the formulation's
  // Recommended list on every formulation change — mirrors MedicineForm.
  // 0-unit case clears the previous pick so a stale unit doesn't
  // linger when switching between medicines.
  useEffect(() => {
    // If the formulation catalog hasn't landed yet, `availableUnits` is
    // still []. Don't run the reset logic in that window — it would
    // blank the record's saved unit before the catalog arrives, and the
    // later "preserve on load" branches (below) would then keep that
    // blank forever. Wait for a real catalog entry before deciding.
    if (!catalogEntry) return;
    if (availableUnits.length === 1) {
      // Same preserve-None rule as the `> 1` branch below: an empty
      // (None) saved unit stays empty on Update; only a truthy-but-
      // stale unit gets replaced with the single Recommended one.
      const only = availableUnits[0];
      const keep = (prev) => (!prev || availableUnits.includes(prev) ? prev : only);
      setDoseUnit(keep);
      setPerSlotQuantities((prev) => {
        const next = {};
        Object.keys(prev).forEach((slot) => {
          next[slot] = { value: prev[slot].value, unit: keep(prev[slot].unit) };
        });
        return next;
      });
    } else if (availableUnits.length > 1) {
      // Preserve the saved unit for the Update form — including an
      // explicit None (empty string) that the reviewer chose. Only replace
      // a TRUTHY-but-stale unit (a value that no longer belongs to the
      // Recommended set for the current formulation) with the first
      // Recommended default. None ("") is a legitimate persisted state,
      // not a "missing" value.
      const first = availableUnits[0];
      const keep = (prev) => (!prev || availableUnits.includes(prev) ? prev : first);
      setDoseUnit(keep);
      setPerSlotQuantities((prev) => {
        const next = {};
        Object.keys(prev).forEach((slot) => {
          next[slot] = { value: prev[slot].value, unit: keep(prev[slot].unit) };
        });
        return next;
      });
    } else {
      setDoseUnit("");
      setPerSlotQuantities((prev) => {
        const next = {};
        Object.keys(prev).forEach((slot) => {
          next[slot] = { value: prev[slot].value, unit: "" };
        });
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicine.formulation, availableUnits.join("|")]);
  // Keep the instruction pick consistent with the formulation's
  // declared options. Instructions are optional (user may pick or
  // clear any chip), so we never auto-select — we only clear a pick
  // that no longer belongs to the current formulation's chip set.
  useEffect(() => {
    if (availableInstructions.length === 0) {
      if (instructions) setInstructions("");
      return;
    }
    if (instructions && !availableInstructions.some((o) => o.key === instructions)) {
      setInstructions("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicine.formulation, availableInstructions.length, availableInstructions.map((o) => o.key).join("|")]);
  const timesOfDayHasAsNeeded = timesOfDay.includes(AS_NEEDED);
  const namedSlots = timesOfDay
    .filter((t) => t !== AS_NEEDED)
    .sort((a, b) => TIMES_OF_DAY.indexOf(a) - TIMES_OF_DAY.indexOf(b));
  // "None" unit (empty string) opts the slot out of the dose check —
  // the Qty box is greyed / disabled in that state, so requiring a
  // positive number would leave a legitimate None-unit save unreachable.
  const scalarDoseOK = !doseUnit || isPositiveDose(doseValue);
  const perSlotDoseOK =
    namedSlots.length === 0 ||
    namedSlots.every((s) => {
      const q = perSlotQuantities[s] || {};
      return !q.unit || isPositiveDose(q.value);
    });
  const hasDose =
    frequencyMode === "timesOfDay"
      ? (timesOfDayHasAsNeeded ? scalarDoseOK : perSlotDoseOK)
      : scalarDoseOK;

  // Dirty check — Save is disabled until the clinician actually changes
  // something. Prevents no-op audit noise when the modal is opened and
  // closed without edits. Compares CURRENT form state to the mount-time
  // snapshot in initialRef.
  const arrEq = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };
  const init = initialRef.current;
  const isDirty =
    status !== init.status ||
    medStartDate !== init.medStartDate ||
    discontinuedDate !== init.discontinuedDate ||
    ongoing !== init.ongoing ||
    durationDays !== init.durationDays ||
    customDays !== init.customDays ||
    doseValue !== init.doseValue ||
    doseUnit !== init.doseUnit ||
    JSON.stringify(perSlotQuantities) !== init.perSlotQuantitiesJSON ||
    frequencyMode !== init.frequencyMode ||
    !arrEq(timesOfDay, init.timesOfDay) ||
    customFreqValue !== init.customFreqValue ||
    customFreqUnit !== init.customFreqUnit ||
    instructions !== init.instructions ||
    diagnosisId !== init.diagnosisId ||
    reason !== init.reason;

  // Same required-field rule as the Add sheet — with a carve-out for the
  // regimen block when the course is being ended: dose / duration /
  // instructions / start-date belong to an ACTIVE regimen, so completing
  // or discontinuing a medicine shouldn't demand they be non-empty (the
  // clinician might be recording a legacy row where they weren't set).
  // ZY-10352 — Ongoing accepts any future start date (planning /
  // scheduled regimen); terminated statuses still can't start in the
  // future. Lower bound of 1900 applies to both.
  const startDateInvalid =
    !!medStartDate &&
    (medStartDate < "1900-01-01" ||
      (status !== "ongoing" && medStartDate > todayISO()));
  // ZY-10352 wave-2 — Discontinued date rules (only enforced when
  // Discontinued is the picked status):
  //   • required
  //   • ≥ medStartDate
  //   • < derivedEndDate (strict — can't discontinue on or after the
  //     scheduled end; cap at today when the regimen is ongoing-
  //     indefinite and has no derived end date).
  const discontinuedDateInvalid =
    showDiscontinuedDate &&
    !!discontinuedDate &&
    discontinuedDate < "1900-01-01";
  const discontinuedDateBeforeStart =
    showDiscontinuedDate &&
    !!discontinuedDate &&
    !!medStartDate &&
    discontinuedDate < medStartDate;
  const discontinuedDateAfterEnd =
    showDiscontinuedDate &&
    !!discontinuedDate &&
    (
      (derivedEndDate && discontinuedDate >= derivedEndDate) ||
      (!derivedEndDate && discontinuedDate > todayISO())
    );
  // ZY-10352 — Completed is cron-managed; block the save here as a
  // defensive layer behind the Select's isDisabled, so a stale state
  // can't slip past the UI (e.g. duration edited AFTER picking
  // Completed moves the end date into the future).
  const completedBeforeEnd = status === "completed" && !isAfterEndDate;
  const canSubmit =
    !readOnly &&
    isDirty &&
    (!showDiscontinuedDate || (
      !!discontinuedDate &&
      !discontinuedDateInvalid &&
      !discontinuedDateBeforeStart &&
      !discontinuedDateAfterEnd
    )) &&
    !completedBeforeEnd &&
    customFreqPairOK &&
    !submitting &&
    !blockedBySibling &&
    (isEnded ||
      (hasDose &&
        !!medStartDate &&
        !startDateInvalid &&
        hasFrequency &&
        (ongoing || effectiveDurationDays > 0)));

  const close = () => onClose(formId);

  const submit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setFeedback(null);
    onSavingChange(formId, true);

    const svc = new MedicineService();
    const done = () => {
      if (!mounted.current) return;
      onSavingChange(formId, false);
      setSubmitting(false);
      EventService.emit(MED_V2_REFRESH);
      onClose(formId);
    };
    const failed = (err) => {
      if (!mounted.current) return;
      onSavingChange(formId, false);
      setSubmitting(false);
      if (err && err.reason === "version") EventService.emit(MED_V2_REFRESH);
      setFeedback({
        kind: "err",
        text: (err && err.message) || "Update failed — please retry",
      });
    };
    // Only ONE dose shape carried per save (see MedicineForm's submit
    // body for the full rationale). Times-of-day + named slots →
    // perSlotQuantities; everything else → scalar doseQuantity.
    const usePerSlot = frequencyMode === "timesOfDay" && namedSlots.length > 0;
    const perSlotClean = usePerSlot
      ? namedSlots.reduce((acc, s) => {
          const q = perSlotQuantities[s] || {};
          acc[s] = { value: String(q.value || "").trim(), unit: String(q.unit || "").trim() };
          return acc;
        }, {})
      : undefined;
    // Times-of-day + As-needed carries a real scalar (updated spec).
    // Ointment / Custom mode also scalar. Per-slot mode leaves the
    // scalar undefined.
    const scalarDose = usePerSlot
      ? undefined
      : { value: doseValue.trim(), unit: doseUnit.trim() };

    const body = {
      status,
      // ZY-10352 wave-2 — the form no longer carries a user-input
      // "Updated on"; sinceWhen is just the wall-clock of the write
      // (backend also stamps UpdatedAt = now on the live-entry mutation).
      // Kept on the wire for validator compatibility.
      sinceWhen: toIsoUtc(new Date()),
      medStartDate: medStartDate ? toIsoUtc(medStartDate) : undefined,
      // On terminate the backend's status-flip semantics own the end date.
      medEndDate: undefined,
      // Discontinue-only: the reviewer-picked effective date. Backend
      // stamps it onto the live entry's DiscontinuedDate; the audit's
      // "Discontinued ON" column reads it directly.
      discontinuedDate:
        status === "discontinued" && discontinuedDate
          ? toIsoUtc(discontinuedDate)
          : undefined,
      ongoingIndefinitely: ongoing,
      durationDays: effectiveDurationDays,
      doseQuantity: scalarDose,
      perSlotQuantities: perSlotClean,
      // Interval chips are UI shortcuts for the (value+unit) pair, so
      // there's no separate `interval` on the wire. Custom mode sends
      // the pair verbatim; Times-of-day sends only slots.
      doseFrequency: frequencyMode === "timesOfDay"
        ? { timesOfDay, customValue: undefined, customUnit: "" }
        : {
            timesOfDay: [],
            customValue: customFreqValue ? parseInt(customFreqValue, 10) || 0 : undefined,
            customUnit: customFreqUnit,
          },
      instructions,
      diagnosisId: diagnosisId || undefined,
      description: reason.trim(),
    };
    svc.list(
      patientId,
      (doc) => {
        svc.updateStatus(
          patientId,
          medicine.medicineId,
          { ...body, expectedVersion: (doc && doc.version) || 0 },
          done,
          failed
        );
      },
      failed
    );
  };

  const band = status ? medicineBand(status) : null;
  const foldId = `med3-sfold-${formId}`;
  const statusId = `med3-sstat-${formId}`;
  const startId = `med3-sstart-${formId}`;
  const endId = `med3-send-${formId}`;
  const discId = `med3-sdisc-${formId}`;
  const reasonId = `med3-sreason-${formId}`;

  return (
    <section className={`med3-dlg${collapsed ? " is-collapsed" : ""}`}>
      <div className="med3-lbl-row med3-handle">
        <span className="med3-lbl med3-update-title">
          {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
          Update
          <span className="med3-update-sub">&middot; {medicine.medicine}</span>
        </span>
        <button
          type="button"
          className="med3-chev"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={foldId}
          aria-label={
            collapsed ? `Expand ${medicine.medicine}` : `Collapse ${medicine.medicine}`
          }
        >
          <CollapseChevron />
        </button>
      </div>

      <div className="med3-fold" id={foldId}>
        {/* ZY-10352 wave-2 — Past medicines (Completed / Discontinued /
            Deleted) render as READ-ONLY: `is-readonly` greys every
            `.med3-field` inside the inner wrapper (dates, discontinued
            date, description, status chip row) with the same
            opacity/saturate/pointer-events recipe the regimen block
            already uses. Footer buttons sit OUTSIDE the greyed scope
            so Close stays active. */}
        <div className={`med3-fold-inner${readOnly ? " is-readonly" : ""}`}>
          {/* ── Status — the three-value select with the band dot. Frozen
              for past medicines (Completed / Discontinued): use the Add
              flow's mCode dedup to reopen / reclassify instead. ── */}
          <div
            className={`med3-field med3-fld${band ? " has-dot" : ""}`}
            style={band ? { "--med3-dot": band.dot } : undefined}
          >
            <label className="med3-lbl" htmlFor={statusId}>
              Status <span className="cc3-req">*</span>
              {wasPast && (
                <span className="med3-lbl-hint">
                  &middot; locked · use Add medicine to reopen
                </span>
              )}
            </label>
            <Select
              id={statusId}
              value={status}
              options={MEDICINE_STATUS}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              onChange={changeStatus}
              disabled={statusDisabled}
              // ZY-10352 Req 3 — "Completed" is cron-managed; the
              // reviewer can only pick it manually once today is at or
              // past the derived end date (i.e. the course has in fact
              // ended). Grayed-out otherwise so a mis-click can't
              // short-circuit the auto-flip.
              isDisabled={(opt) => opt.key === "completed" && !isAfterEndDate}
              // Select portals its menu to <body>; inside the transcript
              // modal the portaled menu's default z-index (200) sits
              // beneath the overlay (1500), so clicks on options land on
              // the backdrop instead. Transcript mode passes "ait-menu"
              // through so the aiTranscript.scss `body > .zy-select-menu.
              // ait-menu { z-index: 1600 }` rule applies. Standalone use
              // leaves it undefined and the Select renders as before.
              menuClassName={menuClassName}
            />
          </div>

          {/* ── Regimen block — everything from For through Duration greys
              out when the course is being ended. Wraps in one div so a
              single `is-locked` class can dim + point-events:none the
              whole block from the SCSS. ── */}
          <div className={`med3-regimen${isEnded ? " is-locked" : ""}`}>
          {/* ── For · link to a diagnosed condition (parity with Add) ── */}
          <div className="med3-field">
            <span className="med3-lbl">
              For <span className="med3-lbl-hint">&middot; link to a diagnosed condition</span>
            </span>
            <DiagnosisPicker
              patientId={patientId}
              value={diagnosisId}
              onChange={setDiagnosisId}
              disabled={regimenDisabled}
            />
          </div>

          <div className="med3-field">
            <label className="med3-lbl is-muted" htmlFor={startId}>
              Start date <span className="cc3-req">*</span>
            </label>
            <span className="med3-date">
              <input
                id={startId}
                className="med3-date-input"
                type="date"
                value={medStartDate}
                onChange={(e) => setMedStartDate(e.target.value)}
                min="1900-01-01"
                // ZY-10352 — Ongoing allows a future start date (planning
                // / scheduled regimen); Discontinued and Completed cap at
                // today because a terminated course can't start in the
                // future. "9999-12-31" is the native date picker's
                // practical ceiling.
                max={status === "ongoing" ? "9999-12-31" : todayISO()}
                disabled={regimenDisabled}
              />
              <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
            </span>
            {/* Only render the error when the input is actionable. When
                Status is terminal (regimen locked), the field is
                greyed-out and the reviewer can't edit it anyway —
                surfacing a validation error there just confuses the
                UX. The copy adapts to the actual trigger so the
                message isn't misleading when the real issue is a
                stored start-date that happens to sit in the future
                (restore-on-terminate copies the stored value verbatim). */}
            {startDateInvalid && !regimenDisabled && (
              <p className="fam-field-error">
                {medStartDate < "1900-01-01"
                  ? "Start date must be on or after 1 Jan 1900."
                  : "Start date cannot be in the future for a terminated medicine."}
              </p>
            )}
          </div>

          {/* ── Dose frequency — mode toggle + nested card with dose
              quantity inside. Mirrors MedicineForm's redesigned layout
              (mode toggle outside; frequency + dose belong to the same
              bordered card regardless of mode). Regimen-disabled state
              propagates to every input so the whole block locks when
              the course is being ended. ── */}
          <div className="med3-field is-roomy">
            <span className="med3-lbl">
              Dose frequency <span className="cc3-req">*</span>
            </span>
            <div className="med3-mode" role="tablist" aria-label="Dose frequency mode">
              <button
                type="button"
                role="tab"
                aria-selected={frequencyMode === "timesOfDay"}
                className={`med3-mode-btn${frequencyMode === "timesOfDay" ? " is-on" : ""}`}
                onClick={() => switchFrequencyMode("timesOfDay")}
                disabled={regimenDisabled}
              >
                Times of day
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={frequencyMode === "custom"}
                className={`med3-mode-btn${frequencyMode === "custom" ? " is-on" : ""}`}
                onClick={() => switchFrequencyMode("custom")}
                disabled={regimenDisabled}
              >
                Custom
              </button>
            </div>

            <div className="med3-freq-card">
              {frequencyMode === "timesOfDay" ? (
                <>
                  <span className="med3-freq-card-lbl">
                    Times of day <span className="cc3-req">*</span>{" "}
                    <span className="med3-lbl-hint">&middot; multi-select</span>
                  </span>
                  <div className="med3-slot-row" role="group">
                    {TIMES_OF_DAY.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`med3-slot-btn${timesOfDay.includes(t) ? " is-on" : ""}`}
                        aria-pressed={timesOfDay.includes(t)}
                        onClick={() => toggleTimeOfDay(t)}
                        disabled={regimenDisabled}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <p className="med3-freq-or"><span>or</span></p>
                  <button
                    type="button"
                    className={`med3-asneeded-btn${timesOfDayHasAsNeeded ? " is-on" : ""}`}
                    aria-pressed={timesOfDayHasAsNeeded}
                    onClick={() => toggleTimeOfDay(AS_NEEDED)}
                    disabled={regimenDisabled}
                  >
                    As needed
                  </button>
                </>
              ) : (
                <>
                  <span className="med3-freq-card-lbl">
                    Interval <span className="cc3-req">*</span>{" "}
                    <span className="med3-lbl-hint">&middot; single-select</span>
                  </span>
                  <div className="med3-slot-row" role="group">
                    {INTERVAL_OPTIONS.map((iv) => {
                      const on = intervalChipMatches(iv);
                      return (
                        <button
                          key={iv}
                          type="button"
                          className={`med3-slot-btn${on ? " is-on" : ""}`}
                          aria-pressed={on}
                          onClick={() => pickInterval(iv)}
                          disabled={regimenDisabled}
                        >
                          {iv}
                        </button>
                      );
                    })}
                  </div>
                  <div className="med3-two">
                    <div className="med3-field">
                      <input
                        className="med3-input"
                        type="number"
                        min={1}
                        max={customFreqMax(customFreqUnit)}
                        value={customFreqValue}
                        onChange={(e) => changeCustomFreqValue(e.target.value)}
                        placeholder="—"
                        aria-label="Custom frequency value"
                        disabled={regimenDisabled}
                      />
                    </div>
                    <div className="med3-field">
                      <Select
                        value={customFreqUnit}
                        options={FREQ_UNIT_OPTIONS.map((u) => ({ key: u, label: u }))}
                        getKey={(o) => o.key}
                        getLabel={(o) => o.label}
                        onChange={changeCustomFreqUnit}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* ── Dose quantity subsection — nested inside the card. */}
              <div className="med3-dose-head med3-dose-head-nested">
                <span className="med3-freq-card-lbl">
                  Dose quantity<span className="cc3-req">*</span>
                </span>
                <span className="med3-formulation-pill" title="Formulation from the catalog">
                  <span className="med3-formulation-pill-lbl">FORMULATION</span>
                  <span className="med3-formulation-pill-val">
                    {medicine.formulationName
                      || (catalogEntry && catalogEntry.label)
                      || medicine.formulation
                      || "—"}
                  </span>
                </span>
              </div>

              {frequencyMode === "timesOfDay" ? (
                timesOfDayHasAsNeeded ? (
                  // As-needed slot picked: scalar Qty + unit row (no
                  // preset ladder). Same 0/1/N unit renderer.
                  <div className="med3-dose-row">
                    {/* Qty stays disabled while the unit is empty
                        (including an explicit "None") — a quantity
                        without a unit is never a meaningful dose. */}
                    <input
                      className="med3-input"
                      type="text"
                      value={doseValue}
                      onChange={(e) => setDoseValue(clampDoseValue(e.target.value))}
                      placeholder="Qty"
                      aria-label="Dose quantity"
                      disabled={regimenDisabled || !doseUnit}
                    />
                    <UnitPicker
                      value={doseUnit}
                      recommended={availableUnits}
                      other={otherUnits}
                      onChange={(u) => {
                        setDoseUnit(u);
                        // Picking None clears the Qty box — a stale
                        // number sitting in a greyed-out input reads as
                        // a live value on save/reload otherwise.
                        if (!u) setDoseValue("");
                      }}
                      disabled={regimenDisabled}
                      ariaLabel="Dose unit"
                    />
                  </div>
                ) : namedSlots.length === 0 ? (
                  <p className="med3-dose-hint">Select times of day to set quantities</p>
                ) : (
                  <div className="med3-slot-doses">
                    {namedSlots.map((slot) => {
                      const q = perSlotQuantities[slot] || { value: "", unit: "" };
                      return (
                        <div className="med3-slot-dose-row" key={slot}>
                          <span className="med3-slot-dose-lbl">{slot}</span>
                          <input
                            className="med3-input"
                            type="text"
                            value={q.value}
                            onChange={(e) => setSlotValue(slot, clampDoseValue(e.target.value))}
                            placeholder="Qty"
                            aria-label={`${slot} dose quantity`}
                            disabled={regimenDisabled || !q.unit}
                          />
                          <UnitPicker
                            value={q.unit}
                            recommended={availableUnits}
                            other={otherUnits}
                            onChange={(u) => setSlotUnit(slot, u)}
                            disabled={regimenDisabled}
                            ariaLabel={`${slot} dose unit`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                <>
                  <SegStrip
                    rows={[DOSE_QUICKPICK.map((v) => ({ key: v, label: v }))]}
                    isOn={(k) => doseValue === k}
                    onToggle={(k) => setDoseValue(doseValue === k ? "" : k)}
                    disabled={regimenDisabled || !doseUnit}
                    boldOn
                  />
                  <div className="med3-dose-row">
                    <input
                      className="med3-input"
                      type="text"
                      value={doseValue}
                      onChange={(e) => setDoseValue(clampDoseValue(e.target.value))}
                      placeholder="Qty"
                      aria-label="Dose quantity"
                      disabled={regimenDisabled || !doseUnit}
                    />
                    <UnitPicker
                      value={doseUnit}
                      recommended={availableUnits}
                      other={otherUnits}
                      onChange={(u) => {
                        setDoseUnit(u);
                        // Picking None clears the Qty box — a stale
                        // number sitting in a greyed-out input reads as
                        // a live value on save/reload otherwise.
                        if (!u) setDoseValue("");
                      }}
                      disabled={regimenDisabled}
                      ariaLabel="Dose unit"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Instructions ── driven by per-formulation options.
              Standard three chips, single "Local application" for
              ointment-family, or hidden entirely for the future
              "None" case. See MedicineForm for the heuristic. */}
          {availableInstructions.length > 0 && (
            <div className="med3-field">
              <span className="med3-lbl">Instructions</span>
              <div className="med3-chips" role="group" aria-label="Instructions">
                {availableInstructions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={`med3-chip${instructions === o.key ? " is-on" : ""}`}
                    aria-pressed={instructions === o.key}
                    onClick={() =>
                      setInstructions(instructions === o.key ? "" : o.key)
                    }
                    disabled={regimenDisabled}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="med3-field is-roomy">
            <div className="med3-duration-head">
              <span className="med3-lbl">
                Duration <span className="cc3-req">*</span>{" "}
                <span className="med3-lbl-hint">&middot; days / weeks</span>
              </span>
              <label className="med3-toggle-wrap">
                <input
                  type="checkbox"
                  checked={ongoing}
                  onChange={(e) => setOngoing(e.target.checked)}
                  disabled={regimenDisabled}
                />
                <span className="med3-toggle" aria-hidden="true" />
                <span className="med3-toggle-lbl">Ongoing</span>
              </label>
            </div>
            <div className={`med3-duration-stack${ongoing ? " is-dimmed" : ""}`}>
              <SegStrip
                rows={[
                  DURATION_DAYS_QUICKPICK.map((d) => ({ key: String(d), label: String(d) })),
                  DURATION_LONG_QUICKPICK.map((d) => ({ key: d.label, label: d.label })),
                ]}
                isOn={(k) => {
                  if (customDays || ongoing) return false;
                  const long = DURATION_LONG_QUICKPICK.find((d) => d.label === k);
                  if (long) return durationDays === long.days && lastPickRef.current === k;
                  return durationDays === Number(k) && lastPickRef.current === k;
                }}
                onToggle={(k) => {
                  if (ongoing) return;
                  lastPickRef.current = k;
                  const long = DURATION_LONG_QUICKPICK.find((d) => d.label === k);
                  pickDays(long ? long.days : Number(k));
                }}
                disabled={ongoing || regimenDisabled}
                footer={
                  <div className="med3-seg-row med3-seg-custom-row">
                    <input
                      className="med3-seg-custom-input"
                      type="number"
                      min="1"
                      value={customDays}
                      onChange={(e) => setCustomDays(e.target.value)}
                      placeholder="Custom - e.g. 21"
                      aria-label="Custom days"
                      disabled={ongoing || regimenDisabled}
                    />
                    <span className="med3-seg-custom-unit">Days</span>
                  </div>
                }
              />
            </div>
          </div>
          </div>

          {/* ZY-10352 wave-2 — End date is a derived, read-only display
              for every status. Shows the regimen's scheduled end
              (medStartDate + duration - 1); renders "Ongoing" copy when
              the regimen is ongoing-indefinite. The reviewer never
              types here — duration + start-date edits upstream drive
              the value — so `is-locked` is applied UNCONDITIONALLY
              (including the Ongoing case). Matches the regimen block's
              opacity/saturate styling so End date reads uniformly as a
              frozen derived display rather than a plain input. */}
          <div className="med3-field is-locked">
            <label className="med3-lbl is-muted" htmlFor={endId}>
              End date
            </label>
            <span className="med3-date">
              <input
                id={endId}
                className="med3-date-input"
                type="date"
                value={derivedEndDate || ""}
                readOnly
                disabled
              />
              <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
            </span>
            {!derivedEndDate && (
              <p className="med3-lbl-hint">
                Regimen is ongoing-indefinite — no end date.
              </p>
            )}
          </div>

          {/* ZY-10352 wave-2 — Discontinued date surfaces ONLY when the
              reviewer picks Discontinued. Required, bounded
              [medStartDate, derivedEndDate - 1 day] (cap at today for
              ongoing-indefinite). Backend stamps the value onto the
              live entry's DiscontinuedDate so the audit's "Discontinued
              ON" column renders the clinical effective date — separate
              from UpdatedAt which captures the wall-clock of the write. */}
          {showDiscontinuedDate && (
            <div className="med3-field">
              <label className="med3-lbl is-muted" htmlFor={discId}>
                Discontinued date <span className="cc3-req">*</span>
              </label>
              <span className="med3-date">
                <input
                  id={discId}
                  className="med3-date-input"
                  type="date"
                  value={discontinuedDate}
                  onChange={(e) => setDiscontinuedDate(e.target.value)}
                  min={medStartDate && medStartDate > "1900-01-01" ? medStartDate : "1900-01-01"}
                  max={discontinuedDateMax}
                  disabled={submitting || readOnly}
                />
                <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
              </span>
              {/* Validation errors are editor-time guidance — they
                  only make sense when the reviewer can actually fix
                  the value. Past-medicine views (readOnly) seed from
                  the stored StatusEntry and leave the Start date at
                  today, which would otherwise falsely trip the
                  before-start check; suppress every discontinued-date
                  error there. */}
              {!readOnly && discontinuedDateInvalid && (
                <p className="fam-field-error">
                  Discontinued date must be on or after 1 Jan 1900.
                </p>
              )}
              {!readOnly && discontinuedDateBeforeStart && !discontinuedDateInvalid && (
                <p className="fam-field-error">
                  Discontinued date cannot be earlier than the medicine&rsquo;s Start date.
                </p>
              )}
              {!readOnly && discontinuedDateAfterEnd && !discontinuedDateInvalid && !discontinuedDateBeforeStart && (
                <p className="fam-field-error">
                  Discontinued date must be earlier than the medicine&rsquo;s End date.
                </p>
              )}
            </div>
          )}

          <div className="med3-field">
            <label className="med3-lbl med3-desc-lbl" htmlFor={reasonId}>
              Description <span className="med3-lbl-hint">&middot; optional</span>
              <span className="med3-words">
                {reason
                  ? `${countWords(reason)} / ${MAX_DESCRIPTION_WORDS} words`
                  : `Max ${MAX_DESCRIPTION_WORDS} words`}
              </span>
            </label>
            <textarea
              id={reasonId}
              className="med3-textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))}
              placeholder="Provide description here"
              disabled={submitting || readOnly}
            />
          </div>

          {feedback && (
            <div className={`cc-feedback is-${feedback.kind}`}>{feedback.text}</div>
          )}

          <footer className="med3-foot">
            {/* Never disabled while saving — see the Add sheet. ZY-10352
                wave-2: past medicines render read-only (Completed /
                Discontinued / Deleted), so the Update button is
                suppressed entirely and the Cancel button simply closes
                the sheet. */}
            <button type="button" className="med3-btn med3-btn-sec" onClick={close}>
              {readOnly ? "Close" : submitting ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button
                type="button"
                className="med3-btn med3-btn-pri"
                onClick={submit}
                disabled={!canSubmit}
                title={blockedBySibling ? "Another medicine is saving" : undefined}
              >
                {submitting ? "Updating…" : "Update"}
              </button>
            )}
          </footer>
        </div>
      </div>
    </section>
  );
};

MedicineStatusForm.propTypes = {
  patientId: PropTypes.number,
  formId: PropTypes.number.isRequired,
  /** The medicine being updated (carries the rail's mode marker). */
  editing: PropTypes.object.isRequired,
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func.isRequired,
  onSavingChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Extra class forwarded to the Status Select's portaled menu so the
   *  transcript modal can bump it above the overlay (see aiTranscript.scss
   *  `body > .zy-select-menu.ait-menu`). Standalone mounts omit. */
  menuClassName: PropTypes.string,
};
MedicineStatusForm.defaultProps = {
  menuClassName: "",
};

export default MedicineStatusForm;
